import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import './Admin.css';

function AdminStaff() {
    const [staff, setStaff] = useState([]);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filterRole, setFilterRole] = useState('');
    const [filterActive, setFilterActive] = useState('');
    const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
    const [message, setMessage] = useState({ type: '', text: '' });

    // Modal states
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showPermissionModal, setShowPermissionModal] = useState(false);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [selectedStaff, setSelectedStaff] = useState(null);

    // Form states
    const [formData, setFormData] = useState({
        username: '', password: '', fullname: '', role_id: '', is_active: true
    });

    // Permission states
    const [allPermissions, setAllPermissions] = useState({ grouped: {}, categories: [] });
    const [userPermissions, setUserPermissions] = useState({ role_permissions: [], special_permissions: [] });

    const fetchStaff = useCallback(async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams({
                page: pagination.page,
                limit: pagination.limit,
                search,
                role_id: filterRole,
                is_active: filterActive
            });

            const [staffRes, rolesRes] = await Promise.all([
                api.get(`/staff?${params}`),
                api.get('/roles')
            ]);

            setStaff(staffRes.data.staff);
            setPagination(staffRes.data.pagination);
            setRoles(rolesRes.data);
        } catch (err) {
            setMessage({ type: 'error', text: 'เกิดข้อผิดพลาดในการโหลดข้อมูล' });
        } finally {
            setLoading(false);
        }
    }, [pagination.page, pagination.limit, search, filterRole, filterActive]);

    useEffect(() => {
        fetchStaff();
    }, [fetchStaff]);

    // Create staff
    const handleCreate = async (e) => {
        e.preventDefault();
        try {
            await api.post('/staff', formData);
            setMessage({ type: 'success', text: 'สร้างเจ้าหน้าที่สำเร็จ' });
            setShowCreateModal(false);
            setFormData({ username: '', password: '', fullname: '', role_id: '', is_active: true });
            fetchStaff();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'เกิดข้อผิดพลาด' });
        }
    };

    // Update staff
    const handleUpdate = async (e) => {
        e.preventDefault();
        try {
            await api.put(`/staff/${selectedStaff.id}`, {
                fullname: formData.fullname,
                role_id: formData.role_id || null,
                is_active: formData.is_active
            });
            setMessage({ type: 'success', text: 'อัพเดทเจ้าหน้าที่สำเร็จ' });
            setShowEditModal(false);
            fetchStaff();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'เกิดข้อผิดพลาด' });
        }
    };

    // Reset password
    const handleResetPassword = async (e) => {
        e.preventDefault();
        try {
            await api.post(`/staff/${selectedStaff.id}/reset-password`, {
                new_password: formData.password
            });
            setMessage({ type: 'success', text: 'รีเซ็ตรหัสผ่านสำเร็จ' });
            setShowPasswordModal(false);
            setFormData({ ...formData, password: '' });
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'เกิดข้อผิดพลาด' });
        }
    };

    // Delete staff
    const handleDelete = async (staffMember) => {
        if (!window.confirm(`ต้องการลบเจ้าหน้าที่ "${staffMember.fullname}" หรือไม่?`)) return;

        try {
            await api.delete(`/staff/${staffMember.id}`);
            setMessage({ type: 'success', text: 'ลบเจ้าหน้าที่สำเร็จ' });
            fetchStaff();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'เกิดข้อผิดพลาด' });
        }
    };

    // Unlock staff
    const handleUnlock = async (staffMember) => {
        try {
            await api.post(`/staff/${staffMember.id}/unlock`);
            setMessage({ type: 'success', text: 'ปลดล็อคบัญชีสำเร็จ' });
            fetchStaff();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'เกิดข้อผิดพลาด' });
        }
    };

    // Open edit modal
    const openEditModal = (staffMember) => {
        setSelectedStaff(staffMember);
        setFormData({
            fullname: staffMember.fullname,
            role_id: staffMember.role_id || '',
            is_active: staffMember.is_active
        });
        setShowEditModal(true);
    };

    // Open permission modal
    const openPermissionModal = async (staffMember) => {
        setSelectedStaff(staffMember);
        try {
            const [permRes, userPermRes] = await Promise.all([
                api.get('/roles/permissions/all'),
                api.get(`/staff/${staffMember.id}/permissions`)
            ]);
            setAllPermissions(permRes.data);
            setUserPermissions(userPermRes.data);
            setShowPermissionModal(true);
        } catch (err) {
            setMessage({ type: 'error', text: 'เกิดข้อผิดพลาดในการโหลดสิทธิ์' });
        }
    };

    // Toggle special permission
    const toggleSpecialPermission = async (permissionId, currentState) => {
        try {
            if (currentState === undefined) {
                await api.post(`/staff/${selectedStaff.id}/permissions`, {
                    permission_id: permissionId,
                    granted: true
                });
            } else if (currentState === true) {
                await api.post(`/staff/${selectedStaff.id}/permissions`, {
                    permission_id: permissionId,
                    granted: false
                });
            } else {
                await api.delete(`/staff/${selectedStaff.id}/permissions/${permissionId}`);
            }

            const userPermRes = await api.get(`/staff/${selectedStaff.id}/permissions`);
            setUserPermissions(userPermRes.data);
        } catch (err) {
            setMessage({ type: 'error', text: 'เกิดข้อผิดพลาด' });
        }
    };

    // Get permission state for staff
    const getPermissionState = (perm) => {
        const roleHas = userPermissions.role_permissions?.some(rp => rp.id === perm.id);
        const special = userPermissions.special_permissions?.find(sp => sp.permission_id === perm.id);

        if (special) {
            return { fromRole: roleHas, special: special.granted };
        }
        return { fromRole: roleHas, special: undefined };
    };

    const categoryLabels = {
        dashboard: 'แดชบอร์ด',
        salary: 'สลิปเงินเดือน',
        tax: 'หนังสือ 50 ทวิ',
        request: 'คำร้อง',
        chat: 'แชท',
        report: 'รายงาน',
        users: 'จัดการผู้ใช้',
        roles: 'จัดการ Role',
        settings: 'ตั้งค่า',
        system: 'ระบบ'
    };

    return (
        <div className="admin-page">
            {/* Breadcrumb */}
            <div className="breadcrumb">
                <Link to="/admin">จัดการระบบ</Link>
                <span>/</span>
                <span>จัดการเจ้าหน้าที่</span>
            </div>

            <div className="admin-header">
                <div>
                    <h1>จัดการเจ้าหน้าที่ระบบ</h1>
                    <p>เจ้าหน้าที่ทั้งหมด {pagination.total} คน (บันทึกใน iMoney DB)</p>
                </div>
                <button className="steam-btn steam-btn-primary" onClick={() => {
                    setFormData({ username: '', password: '', fullname: '', role_id: '', is_active: true });
                    setShowCreateModal(true);
                }}>
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                        <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                    </svg>
                    เพิ่มเจ้าหน้าที่
                </button>
            </div>

            {message.text && (
                <div className={`alert alert-${message.type} mb-3`}>
                    {message.text}
                    <button onClick={() => setMessage({ type: '', text: '' })} style={{ float: 'right', border: 'none', background: 'none', cursor: 'pointer' }}>&times;</button>
                </div>
            )}

            {/* Filters */}
            <div className="steam-card mb-3">
                <div className="steam-card-body">
                    <div className="filters-row">
                        <div className="filter-group">
                            <input
                                type="text"
                                className="steam-input"
                                placeholder="ค้นหาชื่อ, Username..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                        <div className="filter-group">
                            <select
                                className="steam-input"
                                value={filterRole}
                                onChange={(e) => setFilterRole(e.target.value)}
                            >
                                <option value="">-- ทุก Role --</option>
                                {roles.map(role => (
                                    <option key={role.id} value={role.id}>{role.display_name}</option>
                                ))}
                            </select>
                        </div>
                        <div className="filter-group">
                            <select
                                className="steam-input"
                                value={filterActive}
                                onChange={(e) => setFilterActive(e.target.value)}
                            >
                                <option value="">-- ทุกสถานะ --</option>
                                <option value="true">ใช้งาน</option>
                                <option value="false">ระงับ</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            {/* Staff Table */}
            <div className="steam-card">
                <div className="steam-card-body" style={{ padding: 0, overflowX: 'auto' }}>
                    {loading ? (
                        <div className="flex-center" style={{ padding: '40px' }}>
                            <div className="spinner"></div>
                        </div>
                    ) : (
                        <table className="steam-table">
                            <thead>
                                <tr>
                                    <th>เจ้าหน้าที่</th>
                                    <th>Username</th>
                                    <th>Role</th>
                                    <th>สถานะ</th>
                                    <th>เข้าสู่ระบบล่าสุด</th>
                                    <th style={{ width: '180px' }}>จัดการ</th>
                                </tr>
                            </thead>
                            <tbody>
                                {staff.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" style={{ textAlign: 'center', padding: '40px' }}>ไม่พบข้อมูล</td>
                                    </tr>
                                ) : staff.map(s => (
                                    <tr key={s.id}>
                                        <td>
                                            <div className="user-cell">
                                                <div className="user-avatar-small staff-avatar">
                                                    {s.fullname?.charAt(0) || '?'}
                                                </div>
                                                <div>
                                                    <div className="user-name">{s.fullname}</div>
                                                    <div className="user-code">เจ้าหน้าที่ระบบ</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <span className="badge badge-staff">{s.username}</span>
                                        </td>
                                        <td>
                                            {s.role_display ? (
                                                <span className="badge badge-primary">{s.role_display}</span>
                                            ) : (
                                                <span className="text-muted">ไม่ได้กำหนด</span>
                                            )}
                                        </td>
                                        <td>
                                            {s.locked_until ? (
                                                <span className="badge badge-danger">ถูกล็อค</span>
                                            ) : s.is_active ? (
                                                <span className="badge badge-success">ใช้งาน</span>
                                            ) : (
                                                <span className="badge badge-secondary">ระงับ</span>
                                            )}
                                        </td>
                                        <td>
                                            {s.last_login ? new Date(s.last_login).toLocaleString('th-TH') : '-'}
                                        </td>
                                        <td>
                                            <div className="action-buttons">
                                                <button className="action-btn" title="แก้ไข" onClick={() => openEditModal(s)}>
                                                    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                                                        <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34a.9959.9959 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                                                    </svg>
                                                </button>
                                                <button className="action-btn" title="สิทธิ์พิเศษ" onClick={() => openPermissionModal(s)}>
                                                    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                                                        <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/>
                                                    </svg>
                                                </button>
                                                <button className="action-btn" title="รีเซ็ตรหัสผ่าน" onClick={() => {
                                                    setSelectedStaff(s);
                                                    setFormData({ ...formData, password: '' });
                                                    setShowPasswordModal(true);
                                                }}>
                                                    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                                                        <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
                                                    </svg>
                                                </button>
                                                {s.locked_until && (
                                                    <button className="action-btn success" title="ปลดล็อค" onClick={() => handleUnlock(s)}>
                                                        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                                                            <path d="M12 17c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm6-9h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6h1.9c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm0 12H6V10h12v10z"/>
                                                        </svg>
                                                    </button>
                                                )}
                                                {s.username !== 'terza' && (
                                                    <button className="action-btn danger" title="ลบ" onClick={() => handleDelete(s)}>
                                                        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                                                            <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                                                        </svg>
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Pagination */}
                {pagination.totalPages > 1 && (
                    <div className="pagination">
                        <button
                            className="page-btn"
                            disabled={pagination.page === 1}
                            onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                        >
                            &laquo; ก่อนหน้า
                        </button>
                        <span style={{ margin: '0 16px', color: '#6b7280' }}>
                            หน้า {pagination.page} / {pagination.totalPages}
                        </span>
                        <button
                            className="page-btn"
                            disabled={pagination.page === pagination.totalPages}
                            onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                        >
                            ถัดไป &raquo;
                        </button>
                    </div>
                )}
            </div>

            {/* Create Modal */}
            {showCreateModal && (
                <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
                    <div className="modal modal-md" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>เพิ่มเจ้าหน้าที่ใหม่</h3>
                            <button className="modal-close" onClick={() => setShowCreateModal(false)}>&times;</button>
                        </div>
                        <form onSubmit={handleCreate}>
                            <div className="modal-body">
                                <div className="info-box mb-3">
                                    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
                                    </svg>
                                    <span>เจ้าหน้าที่ระบบจะเข้าสู่ระบบด้วย Username แทนเลขบัตรประชาชน</span>
                                </div>

                                <div className="form-group">
                                    <label className="form-label">Username *</label>
                                    <input
                                        type="text"
                                        className="steam-input"
                                        value={formData.username}
                                        onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                                        required
                                        pattern="[a-zA-Z0-9_]+"
                                        placeholder="ภาษาอังกฤษ ตัวเลข หรือ _ เท่านั้น"
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">ชื่อ-นามสกุล *</label>
                                    <input
                                        type="text"
                                        className="steam-input"
                                        value={formData.fullname}
                                        onChange={(e) => setFormData({ ...formData, fullname: e.target.value })}
                                        required
                                    />
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">รหัสผ่าน *</label>
                                        <input
                                            type="password"
                                            className="steam-input"
                                            value={formData.password}
                                            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                            required
                                            minLength="6"
                                            placeholder="อย่างน้อย 6 ตัวอักษร"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Role</label>
                                        <select
                                            className="steam-input"
                                            value={formData.role_id}
                                            onChange={(e) => setFormData({ ...formData, role_id: e.target.value })}
                                        >
                                            <option value="">-- เลือก Role --</option>
                                            {roles.map(role => (
                                                <option key={role.id} value={role.id}>{role.display_name}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="steam-btn steam-btn-secondary" onClick={() => setShowCreateModal(false)}>
                                    ยกเลิก
                                </button>
                                <button type="submit" className="steam-btn steam-btn-primary">
                                    สร้างเจ้าหน้าที่
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Edit Modal */}
            {showEditModal && selectedStaff && (
                <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
                    <div className="modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>แก้ไขเจ้าหน้าที่</h3>
                            <button className="modal-close" onClick={() => setShowEditModal(false)}>&times;</button>
                        </div>
                        <form onSubmit={handleUpdate}>
                            <div className="modal-body">
                                <p className="text-muted mb-3">
                                    Username: <strong>{selectedStaff.username}</strong>
                                </p>

                                <div className="form-group">
                                    <label className="form-label">ชื่อ-นามสกุล</label>
                                    <input
                                        type="text"
                                        className="steam-input"
                                        value={formData.fullname}
                                        onChange={(e) => setFormData({ ...formData, fullname: e.target.value })}
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">Role</label>
                                    <select
                                        className="steam-input"
                                        value={formData.role_id}
                                        onChange={(e) => setFormData({ ...formData, role_id: e.target.value })}
                                    >
                                        <option value="">-- ไม่กำหนด --</option>
                                        {roles.map(role => (
                                            <option key={role.id} value={role.id}>{role.display_name}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label className="toggle-label">
                                        <input
                                            type="checkbox"
                                            checked={formData.is_active}
                                            onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                        />
                                        <span className="toggle-switch"></span>
                                        เปิดใช้งาน
                                    </label>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="steam-btn steam-btn-secondary" onClick={() => setShowEditModal(false)}>
                                    ยกเลิก
                                </button>
                                <button type="submit" className="steam-btn steam-btn-primary">
                                    บันทึก
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Password Reset Modal */}
            {showPasswordModal && selectedStaff && (
                <div className="modal-overlay" onClick={() => setShowPasswordModal(false)}>
                    <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>รีเซ็ตรหัสผ่าน</h3>
                            <button className="modal-close" onClick={() => setShowPasswordModal(false)}>&times;</button>
                        </div>
                        <form onSubmit={handleResetPassword}>
                            <div className="modal-body">
                                <p className="text-muted mb-3">รีเซ็ตรหัสผ่านสำหรับ: {selectedStaff.fullname}</p>
                                <div className="form-group">
                                    <label className="form-label">รหัสผ่านใหม่</label>
                                    <input
                                        type="password"
                                        className="steam-input"
                                        value={formData.password}
                                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                        required
                                        minLength="6"
                                        placeholder="อย่างน้อย 6 ตัวอักษร"
                                    />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="steam-btn steam-btn-secondary" onClick={() => setShowPasswordModal(false)}>
                                    ยกเลิก
                                </button>
                                <button type="submit" className="steam-btn steam-btn-primary">
                                    รีเซ็ตรหัสผ่าน
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Permission Modal */}
            {showPermissionModal && selectedStaff && (
                <div className="modal-overlay" onClick={() => setShowPermissionModal(false)}>
                    <div className="modal modal-xl" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>สิทธิ์พิเศษ: {selectedStaff.fullname}</h3>
                            <button className="modal-close" onClick={() => setShowPermissionModal(false)}>&times;</button>
                        </div>
                        <div className="modal-body permission-modal-body">
                            <div className="permission-info">
                                <p><strong>Role ปัจจุบัน:</strong> {selectedStaff.role_display || 'ไม่ได้กำหนด'}</p>
                                <p className="text-muted" style={{ fontSize: '13px' }}>
                                    สิทธิ์พิเศษจะเพิ่มหรือลบสิทธิ์นอกเหนือจาก Role ที่กำหนด
                                </p>
                            </div>

                            <div className="permission-legend">
                                <span className="legend-item">
                                    <span className="legend-box from-role"></span> จาก Role
                                </span>
                                <span className="legend-item">
                                    <span className="legend-box granted"></span> เพิ่มพิเศษ
                                </span>
                                <span className="legend-item">
                                    <span className="legend-box revoked"></span> ยกเลิก
                                </span>
                            </div>

                            <div className="permissions-grid">
                                {Object.entries(allPermissions.grouped || {}).map(([category, perms]) => (
                                    <div key={category} className="permission-category">
                                        <h4>{categoryLabels[category] || category}</h4>
                                        <div className="permission-list">
                                            {perms.map(perm => {
                                                const state = getPermissionState(perm);
                                                return (
                                                    <div
                                                        key={perm.id}
                                                        className={`permission-item ${state.fromRole ? 'from-role' : ''} ${state.special === true ? 'special-granted' : ''} ${state.special === false ? 'special-revoked' : ''}`}
                                                    >
                                                        <div className="perm-info">
                                                            <div className="perm-name">{perm.display_name}</div>
                                                            <div className="perm-desc">{perm.description}</div>
                                                        </div>
                                                        <div className="perm-actions">
                                                            {state.fromRole && state.special === undefined && (
                                                                <span className="perm-badge role">จาก Role</span>
                                                            )}
                                                            {state.special === true && (
                                                                <span className="perm-badge granted">เพิ่มพิเศษ</span>
                                                            )}
                                                            {state.special === false && (
                                                                <span className="perm-badge revoked">ยกเลิก</span>
                                                            )}
                                                            <button
                                                                type="button"
                                                                className="btn-perm-toggle"
                                                                onClick={() => toggleSpecialPermission(perm.id, state.special)}
                                                                title={
                                                                    state.special === undefined
                                                                        ? 'เพิ่มสิทธิ์พิเศษ'
                                                                        : state.special === true
                                                                            ? 'เปลี่ยนเป็นยกเลิก'
                                                                            : 'ลบสิทธิ์พิเศษ'
                                                                }
                                                            >
                                                                {state.special === undefined ? '+' : state.special === true ? '−' : '×'}
                                                            </button>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="steam-btn steam-btn-secondary" onClick={() => setShowPermissionModal(false)}>
                                ปิด
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                .admin-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 20px;
                }
                .admin-header h1 { margin: 0 0 4px 0; }
                .admin-header p { margin: 0; color: #6b7280; }
                .filters-row {
                    display: flex;
                    gap: 12px;
                    flex-wrap: wrap;
                }
                .filter-group {
                    flex: 1;
                    min-width: 180px;
                }
                .user-cell {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                }
                .user-code {
                    font-size: 12px;
                    color: #6b7280;
                }
                .staff-avatar {
                    background: linear-gradient(135deg, #8b5cf6, #6366f1) !important;
                }
                .badge-staff {
                    background: linear-gradient(135deg, #8b5cf6, #6366f1);
                    color: white;
                    font-weight: 500;
                }
                .badge-info { background: #e0e7ff; color: #3730a3; }
                .badge-secondary { background: #f3f4f6; color: #4b5563; }
                .action-buttons {
                    display: flex;
                    gap: 4px;
                }
                .action-btn.success { color: #059669; }
                .action-btn.success:hover { background: #d1fae5; }
                .modal-md { max-width: 500px; }
                .modal-lg { max-width: 600px; }
                .modal-xl { max-width: 900px; }
                .modal-sm { max-width: 400px; }
                .form-row {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 16px;
                }
                .info-box {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    padding: 12px 16px;
                    background: #eff6ff;
                    border: 1px solid #bfdbfe;
                    border-radius: 8px;
                    color: #1e40af;
                    font-size: 14px;
                }
                .toggle-label {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    cursor: pointer;
                }
                .toggle-label input { display: none; }
                .toggle-switch {
                    width: 44px;
                    height: 24px;
                    background: #e5e7eb;
                    border-radius: 12px;
                    position: relative;
                    transition: background 0.2s;
                }
                .toggle-switch::after {
                    content: '';
                    position: absolute;
                    top: 2px;
                    left: 2px;
                    width: 20px;
                    height: 20px;
                    background: white;
                    border-radius: 50%;
                    transition: transform 0.2s;
                }
                .toggle-label input:checked + .toggle-switch { background: #3b82f6; }
                .toggle-label input:checked + .toggle-switch::after { transform: translateX(20px); }
                .permission-modal-body {
                    max-height: 65vh;
                    overflow-y: auto;
                    pointer-events: auto;
                }
                .modal { pointer-events: auto; }
                .modal-overlay { pointer-events: auto; }
                .permission-info {
                    background: #f9fafb;
                    padding: 12px;
                    border-radius: 8px;
                    margin-bottom: 16px;
                }
                .permission-info p { margin: 0 0 4px 0; }
                .permission-legend {
                    display: flex;
                    gap: 20px;
                    margin-bottom: 16px;
                    padding: 8px 12px;
                    background: #f3f4f6;
                    border-radius: 6px;
                    font-size: 13px;
                }
                .legend-item {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                }
                .legend-box {
                    width: 14px;
                    height: 14px;
                    border-radius: 3px;
                    border: 2px solid;
                }
                .legend-box.from-role { border-color: #3b82f6; background: #dbeafe; }
                .legend-box.granted { border-color: #10b981; background: #d1fae5; }
                .legend-box.revoked { border-color: #ef4444; background: #fee2e2; }
                .permissions-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
                    gap: 16px;
                }
                .permission-category {
                    background: #f9fafb;
                    border-radius: 8px;
                    padding: 12px;
                }
                .permission-category h4 {
                    margin: 0 0 10px 0;
                    font-size: 14px;
                    color: #374151;
                    padding-bottom: 8px;
                    border-bottom: 1px solid #e5e7eb;
                }
                .permission-list {
                    display: flex;
                    flex-direction: column;
                    gap: 6px;
                }
                .permission-item {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 6px 8px;
                    background: white;
                    border-radius: 4px;
                    border: 2px solid transparent;
                    font-size: 13px;
                }
                .permission-item.from-role { border-color: #93c5fd; background: #eff6ff; }
                .permission-item.special-granted { border-color: #6ee7b7; background: #ecfdf5; }
                .permission-item.special-revoked { border-color: #fca5a5; background: #fef2f2; }
                .perm-info { flex: 1; min-width: 0; }
                .perm-name { font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
                .perm-desc { font-size: 11px; color: #6b7280; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
                .perm-actions {
                    display: flex;
                    align-items: center;
                    gap: 4px;
                    flex-shrink: 0;
                    margin-left: 8px;
                }
                .perm-badge {
                    font-size: 9px;
                    padding: 2px 5px;
                    border-radius: 3px;
                    white-space: nowrap;
                }
                .perm-badge.role { background: #dbeafe; color: #1e40af; }
                .perm-badge.granted { background: #d1fae5; color: #065f46; }
                .perm-badge.revoked { background: #fee2e2; color: #991b1b; }
                .btn-perm-toggle {
                    width: 28px;
                    height: 28px;
                    border: none;
                    background: #3b82f6;
                    color: white;
                    border-radius: 6px;
                    cursor: pointer;
                    font-weight: bold;
                    font-size: 16px;
                    line-height: 1;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.15s ease;
                    position: relative;
                    z-index: 5;
                }
                .btn-perm-toggle:hover { background: #2563eb; transform: scale(1.1); }
                .btn-perm-toggle:active { transform: scale(0.95); }
                .permission-item .perm-actions { pointer-events: auto; }
                @media (max-width: 768px) {
                    .form-row { grid-template-columns: 1fr; }
                    .permissions-grid { grid-template-columns: 1fr; }
                    .admin-header { flex-direction: column; gap: 12px; align-items: flex-start; }
                }
            `}</style>
        </div>
    );
}

export default AdminStaff;
